import React, { useContext, useEffect, useState } from 'react';
import { Context } from "../index";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import InfoBlock from "../components/InfoBlock";
import {autoGetPer, roundToSigns} from "../utils/utils";
import Graph from "../components/Graph";
import Lacks from "../components/Lacks";
import EditEsts from "../components/EditEsts";
import { subjInfo, getSheet } from "../utils/utils";
import FileService from "../service/FileService";

const SubjectInfo = () => {
    const { fileStore, userStore } = useContext(Context);
    const navigate = useNavigate();
    const { id, subject } = useParams();
    const [searchParams] = useSearchParams();
    const [index, setIndex] = useState(Number(searchParams.get("idx") || -1));

    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [fileSheet, setFileSheet] = useState(null);
    const [loadingSheet, setLoadingSheet] = useState(false);
    const [info, setInfo] = useState(null);
    const [cls, setCls] = useState(-1);

    // Валидация id
    const idNum = Number(id);
    const isValidId = !isNaN(idNum) && idNum > 0 && Number.isInteger(idNum);

    // Инициализация файлов при первом рендере
    useEffect(() => {
        if (fileStore.files.length === 0) {
            setLoading(true);
            fileStore.getFiles(userStore.user.id).finally(() => {
                setLoading(false);
                setInitialized(true);
            });
        } else {
            setInitialized(true);
        }
    }, [fileStore, userStore.user.id]);

    // Основной эффект загрузки данных
    useEffect(() => {
        if (!initialized || !isValidId) {
            if (initialized) navigate("/"); // если уже инициализирован, но id невалидный — редирект
            return;
        }

        const currentFileIndex = idNum - 1;
        const currentFile = fileStore.files[currentFileIndex];

        // Если файла нет — редирект
        if (!currentFile) {
            navigate("/");
            return;
        }

        let isCancelled = false;

        const loadSheet = async (fileName) => {
            if (isCancelled) return;

            setLoadingSheet(true);
            setFileSheet(null);

            try {
                const sheet = await getSheet(fileName);
                if (isCancelled) return;

                if (index === -1) {
                    const res0 = await FileService.readFile("findBoundaryDates", {fileName: currentFile.serverName});
                    setIndex(autoGetPer(res0.data))
                }

                const res1 = await FileService.readFile("subjInfoFromXL", { subject, fileName, index });
                if (isCancelled) return;

                if (!res1.data) {
                    if (!isCancelled) navigate("/");
                    return;
                }

                const sInfo = subjInfo(res1.data);
                const res = await FileService.readFile("fileInfo", { fileName });
                if (isCancelled) return;

                const currentInfoText = Object.values(res.data).join(", ");
                fileStore.setCurrentInfo(currentInfoText);

                // Извлекаем класс из currentInfo (если возможно)
                const clsFromInfo = currentInfoText?.split(", ")[2]?.split(" ")[0];
                const clsNum = clsFromInfo ? Number(clsFromInfo) : -1;

                if (!isCancelled) {
                    setCls(clsNum);
                    setFileSheet(sheet);
                    setInfo(sInfo);
                    setLoadingSheet(false);
                }
            } catch (e) {
                if (!isCancelled) {
                    alert(`ОКАК: Произошла ошибка загрузки информации про предмет - ${e?.response?.data?.message || e}`);
                    navigate("/");
                }
            } finally {
                if (!isCancelled) {
                    setLoadingSheet(false);
                }
            }
        };

        loadSheet(currentFile.serverName).catch((e) => {
            if (!isCancelled) {
                alert(`ОКАК: Произошла ошибка загрузки информации про предмет - ${e?.response?.data?.message || e}`);
                navigate("/");
            }
        });

        return () => {
            isCancelled = true;
        };
    }, [initialized, id, subject, navigate, fileStore.files, isValidId, idNum, fileStore]);

    function edit(newInfo) {
        setInfo(subjInfo(newInfo));
    }

    // Проверка для рендера лоадера
    const currentFile = isValidId ? fileStore.files[idNum - 1] : undefined;
    if (loading || !initialized || !isValidId || !currentFile || loadingSheet || !fileSheet || index === -1) {
        return (
            <main style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 75px)' }}>
                <div className="spinner"></div>
            </main>
        );
    }

    return (
        <main>
            {fileStore.currentInfo && <div className="file-info for-phones">{fileStore.currentInfo}</div>}
            <div className="h2">Подробнее про предмет {subject}:</div><br/>
            <span style={{
                    marginLeft: "var(--padding-main-left)",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                    fontSize: "18px"
                }}
                  onClick={() => navigate(index ? `/file/${id}?idx=${index}` : `/file/${id}`)}>
                <i className="icon-arrow-left"></i>Назад
            </span><br/>
            {fileStore.currentInfo?.split(",")[fileStore.currentInfo.split(",").length - 1].trim().toLowerCase() === "год" && (
                <div className="nav">
                    {cls >= 10
                        ? ["I полугодие", "II полугодие", "Год"].map((item, i) => (
                            <a
                                key={i}
                                href={`${subject}?idx=${i + 1}`}
                                className={i + 1 === index ? "active" : ""}
                            >
                                {item}
                            </a>
                        ))
                        : ["I четверть", "II четверть", "III четверть", "IV четверть", "Год"].map((item, i) => (
                            <a
                                key={i}
                                href={`${subject}?idx=${i + 1}`}
                                className={i + 1 === index ? "active" : ""}
                            >
                                {item}
                            </a>
                        ))}
                </div>
            )}
            <div className="all-info" style={{ flexDirection: "column", maxWidth: '' }}>
                <div className='info-blocks'>
                    <InfoBlock
                        title="Текущий балл"
                        subInfo={info.nowScore}
                    >
                        <span style={{ fontFamily: 'Source_Sans3_bold' }}>{info.score2Signs}</span> ~&nbsp;
                        <span style={{ fontFamily: 'Source_Sans3_bold' }} className={info.color}>{info.round}</span>
                    </InfoBlock>
                    <InfoBlock title="Минимальный балл">
                        <span className={info.min.color}>
                            {info.scoreMass.length > 0 ? roundToSigns(info.min.min, 2, false) : "0.00"}
                        </span>
                    </InfoBlock>
                    <InfoBlock title="Максимальный балл">
                        <span className={info.max.color}>
                            {info.scoreMass.length > 0 ? roundToSigns(info.max.max, 2, false) : "0.00"}
                        </span>
                    </InfoBlock>
                    <InfoBlock title="Количество оценок">
                        <span style={{ fontFamily: 'Source_Sans3_bold' }}>{info.info.coeffs.length}</span>
                    </InfoBlock>
                </div>
                <div className='graph_lacks'>
                    <div
                        className="graphBlock"
                        style={
                            info.scoreMass.length <= 1
                                ? {
                                    display: "flex",
                                    textAlign: "center",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "var(--gray2-color)",
                                    fontFamily: "Source_Sans3_bold, sans-serif",
                                    fontSize: "30px"
                                }
                                : {}
                        }
                    >
                        {info.scoreMass.length > 1 && (
                            <div style={{ fontFamily: "Source_Sans3_bold, sans-serif", fontSize: "20px", textAlign: "center" }}>
                                График изменения среднего балла по предмету {subject}
                            </div>
                        )}
                        <Graph scores={info.scoreMass} dates={info.info.dates} estimates={info.info.estimates} />
                    </div>
                    <div className="lacks-cont">
                        <div className="lacks">
                            <div style={{ fontFamily: "Source_Sans3_bold, sans-serif", fontSize: "20px" }}>
                                Сколько осталось до повышения или понижения балла:
                            </div>
                            <Lacks estimates={info.info.estimates} coeffs={info.info.coeffs} />
                        </div>
                    </div>
                </div>
                <EditEsts change={edit} marks={info.info} />
            </div>
        </main>
    );
};

export default observer(SubjectInfo);